import { nextTick, unref } from 'vue'

import {
  hasPracticeRouteId,
  isPracticeActive,
  isPracticeId,
  PRACTICE_QUERY_SCOPE,
} from 'src/composables/onboarding/practice/practiceMode'
import {
  clearPracticeSource,
  getPracticeAccount,
  getPracticeSearchResults,
  getPracticeTicket,
  setPracticeStage,
} from 'src/composables/onboarding/practice/practiceSource'
import { log } from 'src/services/app/log.service'
import {
  PRACTICE_FOLLOWED_TICKET_ID,
  PRACTICE_SELECTION,
  PRACTICE_STAGE,
} from 'src/services/consts/onboarding-practice.const'
import { ONBOARDING_TARGET_WAIT_MS } from 'src/services/consts/onboarding.const'
import { QUERY_KEYS } from 'src/services/consts/queries.const'
import {
  SEARCH_TAB_NAMES,
  TICKET_CREATION_STEP_NAMES,
} from 'src/services/consts/ticket-creation.const'
import { ROUTES } from 'src/router/const'

// The stages where the followed request has a detail to show. practiceSource.js checks the same two
// stages in followedVersion.
const FOLLOWED_TICKET_STAGES = [PRACTICE_STAGE.DRAFT, PRACTICE_STAGE.SUBMITTED]

// Two cards share a screen when their scenes match.
export function getSceneKey(card) {
  const { route, params = {}, query = {}, stage, isRealData = false } = card.scene
  return JSON.stringify([route, params, query, stage, isRealData])
}

// Clears the product step's draft, so a leave never sees example answers as unsaved work. Nothing
// happens on any other page.
function resetProductDraft({ router, newTicketStore }) {
  if (router.currentRoute.value.name !== ROUTES.TICKET_EDIT) return
  if (newTicketStore.step !== TICKET_CREATION_STEP_NAMES.PRODUCT_SEARCH) return
  newTicketStore.resetTicket()
}

// Moves to a card's screen with replace, so walkthrough screens add no history entries.
async function moveToScene({ router, scene }) {
  try {
    const location = { name: scene.route, params: scene.params ?? {}, query: scene.query ?? {} }
    if (router.resolve(location).fullPath === router.currentRoute.value.fullPath) return

    await router.replace(location)
  } catch (error) {
    log.error('Onboarding walkthrough could not open its screen', error)
  }
}

function seedFollowedTicket(queryClient) {
  queryClient.setQueryData(
    [QUERY_KEYS.TICKET, PRACTICE_FOLLOWED_TICKET_ID],
    getPracticeTicket(PRACTICE_FOLLOWED_TICKET_ID)
  )
}

/**
 * Sets up a card's screen behind the dim layer: sets the example stage, writes the example data, and
 * moves to the screen. The card with real data switches the example data off. It does not wait for
 * the targets; the controller does that next.
 */
export async function prepareScreen({ card, router, queryClient, newTicketStore }) {
  const { scene } = card

  setPracticeStage(scene.stage)
  resetProductDraft({ router, newTicketStore })
  if (scene.stage === PRACTICE_STAGE.SELECTED) {
    newTicketStore.resetTicket()
    newTicketStore.setTicketFields(PRACTICE_SELECTION)
    // The results have no setter. The search mutation assigns them the same way.
    newTicketStore.entitySearchResults = getPracticeSearchResults()
    newTicketStore.setActiveTab(SEARCH_TAB_NAMES.PRODUCTS)
  }

  if (scene.isRealData) {
    // The example entries stay in the cache, so Back from this card can show them again.
    isPracticeActive.value = false
    await moveToScene({ router, scene })
    return
  }

  const exampleAccount = getPracticeAccount()
  if (exampleAccount) {
    queryClient.setQueryData([PRACTICE_QUERY_SCOPE, QUERY_KEYS.ACCOUNT_DATA], exampleAccount)
  }
  // Set before the move, so the router keeps the example ids in the address.
  isPracticeActive.value = true
  // Leaving the wizard seeds the followed request after the move. The explain step loads the
  // request's own answers, unchanged, so the leave guard compares them equal and stays quiet.
  const isLeavingWizard =
    router.currentRoute.value.name === ROUTES.TICKET_EDIT && scene.route !== ROUTES.TICKET_EDIT
  const hasFollowedTicket = FOLLOWED_TICKET_STAGES.includes(scene.stage)
  if (hasFollowedTicket && !isLeavingWizard) seedFollowedTicket(queryClient)
  await moveToScene({ router, scene })
  if (hasFollowedTicket && isLeavingWizard) seedFollowedTicket(queryClient)
}

/**
 * Resolves 'ready' once every anchor has a box that stays the same for two frames. Resolves 'missing'
 * after ONBOARDING_TARGET_WAIT_MS, and 'cancelled' when shouldKeepWaiting() turns false. No anchors
 * resolve 'ready' at once.
 */
export function waitForTargets({ anchors, shouldKeepWaiting }) {
  if (anchors.length === 0) return Promise.resolve('ready')

  return new Promise((resolve) => {
    let previousBoxes = ''
    let steadyFrames = 0
    let frameId = null
    let timeoutId = null
    let isSettled = false

    function settle(status) {
      if (isSettled) return
      isSettled = true
      cancelAnimationFrame(frameId)
      clearTimeout(timeoutId)
      resolve(status)
    }

    function checkFrame() {
      if (!shouldKeepWaiting()) return settle('cancelled')

      const boxes = anchors.map((anchor) => {
        const element = document.querySelector(`[data-tour="${anchor}"]`)
        if (!element) return null
        const { x, y, width, height } = element.getBoundingClientRect()
        return width > 0 && height > 0 ? `${x},${y},${width},${height}` : null
      })

      if (boxes.every(Boolean)) {
        const boxKey = boxes.join('|')
        steadyFrames = boxKey === previousBoxes ? steadyFrames + 1 : 0
        previousBoxes = boxKey
        if (steadyFrames >= 2) return settle('ready')
      } else {
        steadyFrames = 0
        previousBoxes = ''
      }

      frameId = requestAnimationFrame(checkFrame)
    }

    timeoutId = setTimeout(() => settle('missing'), ONBOARDING_TARGET_WAIT_MS)
    frameId = requestAnimationFrame(checkFrame)
  })
}

// Removes every example cache entry, then the example source. Only the example entries go: the real
// account entry stays under its own key.
export function clearExampleData({ queryClient }) {
  queryClient.removeQueries({
    predicate: ({ queryKey }) =>
      queryKey.some((part) => part === PRACTICE_QUERY_SCOPE || isPracticeId(unref(part))),
  })
  clearPracticeSource()
}

/**
 * Leaves the example screens. The product draft goes first, while the example data is still on. The
 * example flag then goes off, and the wait lets the account query switch back to the real entry. The
 * caller removes the example entries after this resolves.
 */
export async function leaveScreens({ router, newTicketStore, ticketsStore }) {
  resetProductDraft({ router, newTicketStore })

  const currentRoute = router.currentRoute.value
  const isOnPlainDashboard = currentRoute.name === ROUTES.HOME && !hasPracticeRouteId(currentRoute)
  if (!isOnPlainDashboard) {
    try {
      await router.replace({ name: ROUTES.HOME })
    } catch (error) {
      log.error('Onboarding walkthrough could not return to the Dashboard', error)
    }
  }

  ticketsStore.resetFiltersState()
  isPracticeActive.value = false
  await nextTick()
}
